import classNames from 'classnames';
import React, { useState } from 'react';
import { PostSort } from '../graphql/generated/graphql';
import alphabeticalIcon from '../icons/non-prefilled/16x16/alphabetical.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import newlyCreatedIcon from '../icons/non-prefilled/16x16/newlyCreated.svg';
import popularityIcon from '../icons/non-prefilled/16x16/popularity.svg';
import publishIcon from '../icons/non-prefilled/16x16/publish.svg';
import viewedIcon from '../icons/non-prefilled/16x16/viewed.svg';
import Button from './Button';
import Caret from './Caret';
import {
  MenuItemCheckbox,
  MenuItemRadio,
  MenuSeparator,
  MenuSimpleItem,
} from './Menu';
import OverflowMenu from './OverflowMenu';
import * as styles from './PostSortMenuButton.module.scss';
import SVGIcon from './SVGIcon';

const SORT_OPTIONS = {
  [PostSort.Popularity]: {
    label: 'Most Popular',
    icon: popularityIcon,
  },
  [PostSort.Published]: {
    label: 'Publish Date',
    icon: publishIcon,
  },
  [PostSort.Created]: {
    label: 'Last Created',
    icon: newlyCreatedIcon,
  },
  [PostSort.Edited]: {
    label: 'Last Edited',
    icon: editIcon,
  },
  [PostSort.Alphabetical]: {
    label: 'Alphabetical',
    icon: alphabeticalIcon,
  },
  [PostSort.Viewed]: {
    label: 'Viewed by You',
    icon: viewedIcon,
  },
} as const;

type AllowedPostSort = keyof typeof SORT_OPTIONS;

interface PostSortOverflowMenuProps<T extends AllowedPostSort> {
  allowedPostSorts: T[];
  sortBy: T;
  onSortByChange: (sortBy: T) => void;
  isArchivedIncluded: boolean;
  onIsArchivedIncludedChange: (isArchivedIncluded: boolean) => void;
}

/**
 * A button that opens a menu with the available post sorts.
 */
const PostSortMenuButton = <T extends AllowedPostSort>({
  allowedPostSorts,
  sortBy,
  onSortByChange,
  isArchivedIncluded,
  onIsArchivedIncludedChange,
}: PostSortOverflowMenuProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <OverflowMenu
      placement="bottom-end"
      open={isOpen}
      onOpenChange={setIsOpen}
      button={
        <Button
          className={classNames(styles.button, { [styles.open]: isOpen })}
        >
          <SVGIcon size={14}>{SORT_OPTIONS[sortBy].icon}</SVGIcon>
          <div className={styles.label}>{SORT_OPTIONS[sortBy].label}</div>
          <Caret direction={isOpen ? 'up' : 'down'} />
        </Button>
      }
    >
      <MenuSimpleItem>
        <h5 className={styles.title}>Sort</h5>
      </MenuSimpleItem>
      {allowedPostSorts.map((value) => {
        const option = SORT_OPTIONS[value];
        return (
          <MenuItemRadio
            key={value}
            checked={sortBy === value}
            icon={option.icon}
            onClick={() => onSortByChange(value)}
            variant="highlight"
          >
            {option.label}
          </MenuItemRadio>
        );
      })}
      <MenuSeparator />
      <MenuItemCheckbox
        checked={isArchivedIncluded}
        onClick={() => onIsArchivedIncludedChange(!isArchivedIncluded)}
      >
        Include archived
      </MenuItemCheckbox>
    </OverflowMenu>
  );
};

export default PostSortMenuButton;
